<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="ww-page-header">
            <div class="ww-page-heading">
              <div class="ww-page-title">IoT Alerts</div>
              <div class="ww-page-subtitle">Monitor active alerts and manage alert rules.</div>
            </div>
            <div>
              <v-btn 
                color="secondary" 
                class="mr-2" 
                @click="checkAllAlerts"
                :loading="checkingAlerts"
              >
                <v-icon left>mdi-refresh</v-icon>
                Check Alerts
              </v-btn>
              <v-btn color="primary" @click="showRuleDialog = true">
                <v-icon left>mdi-plus</v-icon>
                Create Alert Rule
              </v-btn>
            </div>
          </v-card-title>

          <v-divider />

          <!-- Tabs -->
          <v-tabs v-model="activeTab" class="tab-buttons">
            <v-tab value="alerts" class="text-none tab-button">Active Alerts</v-tab>
            <v-tab value="rules" class="text-none tab-button">Alert Rules</v-tab>
          </v-tabs>

          <v-window v-model="activeTab">
            <!-- Active Alerts Tab -->
            <v-window-item value="alerts">
              <v-card-text>
                <v-data-table
                  :headers="alertHeaders"
                  :items="alerts"
                  :loading="loadingAlerts"
                  item-key="alertId"
                  class="iot-alerts-table"
                  @click:row="onAlertRowClick"
                >
                  <template #item.severity="{ item }">
                    <v-chip
                      :color="getSeverityColor(item.severity)"
                      size="small"
                      variant="tonal"
                      class="iot-severity-chip"
                    >
                      {{ item.severity }}
                    </v-chip>
                  </template>
                  <template #item.thresholdValue="{ item }">
                    {{ formatNumber(item.thresholdValue) }}
                  </template>
                  <template #item.actualValue="{ item }">
                    {{ formatNumber(item.actualValue) }}
                  </template>
                  <template #item.triggeredAt="{ item }">
                    {{ formatDate(item.triggeredAt) }}
                  </template>
                  <template #item.actions="{ item }">
                    <v-btn
                      icon="mdi-check"
                      size="small"
                      variant="text"
                      @click="acknowledgeAlert(item)"
                      :disabled="!!item.acknowledgedAt"
                    />
                    <v-btn
                      icon="mdi-close"
                      size="small"
                      variant="text"
                      @click="resolveAlert(item)"
                      :disabled="!item.isActive"
                    />
                  </template>
                </v-data-table>
              </v-card-text>
            </v-window-item>

            <!-- Alert Rules Tab -->
            <v-window-item value="rules">
              <v-card-text>
                <v-data-table
                  :headers="ruleHeaders"
                  :items="rules"
                  :loading="loadingRules"
                  item-key="ruleId"
                  class="iot-rules-table"
                  @click:row="onRuleRowClick"
                >
                  <template #item.isEnabled="{ item }">
                    <v-switch
                      v-model="item.isEnabled"
                      density="compact"
                      hide-details
                      @change="updateRule(item)"
                    />
                  </template>
                  <template #item.actions="{ item }">
                    <v-btn
                      icon="mdi-pencil"
                      size="small"
                      variant="text"
                      @click="editRule(item)"
                    />
                    <v-btn
                      icon="mdi-delete"
                      size="small"
                      variant="text"
                      color="error"
                      @click="deleteRule(item)"
                    />
                  </template>
                </v-data-table>
              </v-card-text>
            </v-window-item>
          </v-window>
        </v-card>
      </v-col>
    </v-row>

    <!-- Alert Rule Dialog -->
    <v-dialog v-model="showRuleDialog" max-width="600">
      <v-card>
        <v-card-title>
          {{ editingRule ? "Edit Alert Rule" : "Create Alert Rule" }}
        </v-card-title>
        <v-card-text>
          <v-form ref="ruleForm">
            <v-select
              v-model="ruleForm.deviceId"
              :items="devices"
              item-title="deviceName"
              item-value="deviceId"
              label="Device *"
              :rules="[r.required]"
            />
            <v-select
              v-model="ruleForm.alertType"
              :items="alertTypes"
              item-title="title"
              item-value="value"
              label="Alert Type *"
              :rules="[r.required]"
            />
            <v-select
              v-model="ruleForm.severity"
              :items="severities"
              item-title="title"
              item-value="value"
              label="Severity *"
              :rules="[r.required]"
            />
            <v-text-field
              v-model.number="ruleForm.thresholdValue"
              label="Threshold Value"
              type="number"
              step="0.01"
            />
            <v-select
              v-model="ruleForm.comparisonOperator"
              :items="operators"
              item-title="title"
              item-value="value"
              label="Comparison Operator"
            />
            <v-text-field
              v-model="ruleForm.notificationEmails"
              label="Notification Emails"
              hint="Comma-separated email addresses"
            />
            <v-switch
              v-model="ruleForm.isEnabled"
              label="Enabled"
              color="primary"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeRuleDialog">Cancel</v-btn>
          <v-btn color="primary" @click="saveRule">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {
  getActiveAlerts,
  getAlertRules,
  createAlertRule,
  updateAlertRule,
  deleteAlertRule,
  acknowledgeAlert as acknowledgeAlertApi,
  resolveAlert as resolveAlertApi,
  checkAllDevicesAlerts,
} from "@/api/iotAlerts";
import iotApi from "@/api/iot";
import { useDialogStore } from "@/stores/dialog";

export default {
  name: "IoTAlerts",
  data() {
    return {
      activeTab: "alerts",
      alerts: [],
      rules: [],
      devices: [],
      loadingAlerts: false,
      loadingRules: false,
      checkingAlerts: false,
      showRuleDialog: false,
      editingRule: null,
      ruleForm: {
        deviceId: null,
        alertType: null,
        severity: null,
        thresholdValue: null,
        comparisonOperator: ">",
        notificationEmails: null,
        isEnabled: true,
      },
      alertHeaders: [
        { title: "Device", key: "deviceName" },
        { title: "Type", key: "alertType" },
        { title: "Severity", key: "severity" },
        { title: "Threshold", key: "thresholdValue", width: 100 },
        { title: "Actual", key: "actualValue", width: 100 },
        { title: "Title", key: "title" },
        { title: "Triggered", key: "triggeredAt" },
        { title: "Actions", key: "actions", sortable: false },
      ],
      ruleHeaders: [
        { title: "Device", key: "deviceId" },
        { title: "Alert Type", key: "alertType" },
        { title: "Severity", key: "severity" },
        { title: "Threshold", key: "thresholdValue" },
        { title: "Enabled", key: "isEnabled" },
        { title: "Actions", key: "actions", sortable: false },
      ],
      alertTypes: [
        { title: "High Count/Traffic", value: "HighTraffic" },
        { title: "Low Battery", value: "LowBattery" },
        { title: "Low Voltage", value: "LowVoltage" },
        { title: "No Data", value: "NoData" },
        { title: "Temperature", value: "Temperature" },
      ],
      severities: [
        { title: "Low", value: "Low" },
        { title: "Medium", value: "Medium" },
        { title: "High", value: "High" },
        { title: "Critical", value: "Critical" },
      ],
      operators: [
        { title: "Greater Than (>)", value: ">" },
        { title: "Greater Than or Equal (>=)", value: ">=" },
        { title: "Less Than (<)", value: "<" },
        { title: "Less Than or Equal (<=)", value: "<=" },
        { title: "Equal (==)", value: "==" },
      ],
      r: {
        required: (v) => !!v || "Required",
      },
    };
  },
  methods: {
    async loadAlerts() {
      this.loadingAlerts = true;
      try {
        this.alerts = await getActiveAlerts();
      } catch (err) {
        console.error("Failed to load alerts:", err);
        this.$toast?.error?.("Failed to load alerts");
      } finally {
        this.loadingAlerts = false;
      }
    },
    async loadRules() {
      this.loadingRules = true;
      try {
        this.rules = await getAlertRules();
      } catch (err) {
        console.error("Failed to load rules:", err);
        this.$toast?.error?.("Failed to load alert rules");
      } finally {
        this.loadingRules = false;
      }
    },
    async loadDevices() {
      try {
        const response = await iotApi.getDevices();
        this.devices = response.data || response;
      } catch (err) {
        console.error("Failed to load devices:", err);
      }
    },
    async checkAllAlerts() {
      this.checkingAlerts = true;
      try {
        await checkAllDevicesAlerts();
        this.$toast?.success?.("Alert check completed");
        // Reload alerts to show any new ones
        await this.loadAlerts();
      } catch (err) {
        console.error("Failed to check alerts:", err);
        this.$toast?.error?.("Failed to check alerts");
      } finally {
        this.checkingAlerts = false;
      }
    },
    async acknowledgeAlert(alert) {
      try {
        const userId = this.$store?.state?.auth?.user?.id || "system";
        await acknowledgeAlertApi(alert.alertId, userId);
        this.$toast?.success?.("Alert acknowledged");
        await this.loadAlerts();
      } catch (err) {
        console.error("Failed to acknowledge alert:", err);
        this.$toast?.error?.("Failed to acknowledge alert");
      }
    },
    async resolveAlert(alert) {
      try {
        await resolveAlertApi(alert.alertId);
        this.$toast?.success?.("Alert resolved");
        await this.loadAlerts();
      } catch (err) {
        console.error("Failed to resolve alert:", err);
        this.$toast?.error?.("Failed to resolve alert");
      }
    },
    onAlertRowClick(event, payload) {
      const item = payload?.item ?? payload;
      if (item && !item.acknowledgedAt) this.acknowledgeAlert(item);
    },
    onRuleRowClick(event, payload) {
      const item = payload?.item ?? payload;
      if (item) this.editRule(item);
    },
    editRule(rule) {
      this.editingRule = rule;
      this.ruleForm = {
        deviceId: rule.deviceId,
        alertType: rule.alertType,
        severity: rule.severity,
        thresholdValue: rule.thresholdValue,
        comparisonOperator: rule.comparisonOperator || ">",
        notificationEmails: rule.notificationEmails,
        isEnabled: rule.isEnabled,
      };
      this.showRuleDialog = true;
    },
    async saveRule() {
      const valid = await this.$refs.ruleForm.validate();
      if (!valid) return;

      try {
        if (this.editingRule) {
          await updateAlertRule(this.editingRule.ruleId, this.ruleForm);
          this.$toast?.success?.("Alert rule updated");
        } else {
          await createAlertRule(this.ruleForm);
          this.$toast?.success?.("Alert rule created");
        }
        this.closeRuleDialog();
        await this.loadRules();
      } catch (err) {
        console.error("Failed to save rule:", err);
        this.$toast?.error?.("Failed to save alert rule");
      }
    },
    async deleteRule(rule) {
      const dialog = useDialogStore();
      const result = await dialog.confirm({
        title: "Delete Alert Rule",
        message: `Delete alert rule for ${rule.deviceId}?`,
        variant: "warn",
        buttons: "ok-cancel"
      });
      if (result !== "ok") return;

      try {
        await deleteAlertRule(rule.ruleId);
        this.$toast?.success?.("Alert rule deleted");
        await this.loadRules();
      } catch (err) {
        console.error("Failed to delete rule:", err);
        this.$toast?.error?.("Failed to delete alert rule");
      }
    },
    async updateRule(rule) {
      try {
        await updateAlertRule(rule.ruleId, rule);
      } catch (err) {
        console.error("Failed to update rule:", err);
        this.$toast?.error?.("Failed to update rule");
        await this.loadRules(); // Reload to revert UI
      }
    },
    closeRuleDialog() {
      this.showRuleDialog = false;
      this.editingRule = null;
      this.ruleForm = {
        deviceId: null,
        alertType: null,
        severity: null,
        thresholdValue: null,
        comparisonOperator: ">",
        notificationEmails: null,
        isEnabled: true,
      };
    },
    getSeverityColor(severity) {
      switch (severity?.toLowerCase()) {
        case "critical":
          return "red";
        case "high":
          return "orange";
        case "medium":
          return "yellow";
        case "low":
          return "blue";
        default:
          return "grey";
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return "N/A";
      return new Date(dateStr).toLocaleString();
    },
    formatNumber(val) {
      if (val == null || val === undefined) return "-";
      const n = Number(val);
      return isNaN(n) ? "-" : n.toLocaleString(undefined, { maximumFractionDigits: 2 });
    },
  },
  async mounted() {
    await Promise.all([this.loadAlerts(), this.loadRules(), this.loadDevices()]);
  },
  watch: {
    activeTab(newTab) {
      if (newTab === "alerts") {
        this.loadAlerts();
      } else if (newTab === "rules") {
        this.loadRules();
      }
    },
  },
};
</script>

<style scoped>
.tab-buttons {
  margin-top: 8px;
}

.tab-buttons :deep(.v-tab) {
  background-color: rgb(var(--v-theme-primary));
  color: white !important;
  border-radius: 4px;
  margin-right: 8px;
  padding: 8px 16px;
  font-weight: 500;
  text-transform: none;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
}

.tab-buttons :deep(.v-tab:hover) {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  transform: translateY(-1px);
}

.tab-buttons :deep(.v-tab--selected) {
  background-color: rgb(var(--v-theme-primary));
  color: white !important;
  opacity: 1;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}

.tab-buttons :deep(.v-tab:not(.v-tab--selected)) {
  background-color: rgba(var(--v-theme-primary), 0.6);
  opacity: 0.8;
}

.tab-buttons :deep(.v-tabs-slider) {
  display: none;
}

.iot-alerts-table :deep(.v-data-table__tr),
.iot-rules-table :deep(.v-data-table__tr) {
  cursor: pointer;
}
.iot-alerts-table :deep(.v-data-table__tr:hover),
.iot-rules-table :deep(.v-data-table__tr:hover) {
  background-color: rgba(0, 0, 0, 0.04) !important;
}

/* Fix chip content text not displaying (same as calendar WO nr / WorkOrders grid) */
.iot-alerts-table :deep(.v-chip__content),
.iot-rules-table :deep(.v-chip__content) {
  color: #000000 !important;
}
</style>
